<script setup lang="ts">
import { assetLink } from "@/common/util"
import type { BlogPost } from "@/model/backend/blog-post"
import { $formatDate, useHttpClient } from "@/vf"
import { ref } from "vue"
import { presetImages } from "@/common/assets/preset-images/preset-images"
import { useShopAppConfig } from "@/shop/composables"
/*─────────────────────────────────────┐
│   props                              │
└─────────────────────────────────────*/
/*─────────────────────────────────────┐
│   functions                          │
└─────────────────────────────────────*/
type BlogResponse = {
    blogPosts: BlogPost[]
    readBlogPosts: string[]
}
async function loadMore() {
    const data = (await http.get<BlogResponse>("/blog", { offset: posts.value.length, perPage: perPage.value })).data
    posts.value.push(...data.blogPosts)
    readBlogPostIds.value = data.readBlogPosts
    if (data.blogPosts.length < perPage.value) {
        hasReachedEnd.value = true
    }
}

/*─────────────────────────────────────┐
│   composables                        │
└─────────────────────────────────────*/
const http = useHttpClient()
const { appConfig } = useShopAppConfig()
/*─────────────────────────────────────┐
│   refs                               │
└─────────────────────────────────────*/
const posts = ref<BlogPost[]>([])
const hasReachedEnd = ref(false)
const perPage = ref(5)
const readBlogPostIds = ref<string[]>([])
/*─────────────────────────────────────┐
│   setup                              │
└─────────────────────────────────────*/
await loadMore()
</script>

<template>
    <div class="container" aria-hidden="true">
        <div class="breadcrumb">
            <RouterLink class="breadcrumb-item" :to="{ name: 'home' }">Home</RouterLink>
            <div class="breadcrumb-item">Neuigkeiten</div>
        </div>
    </div>
    <main aria-label="Neuigkeiten">
        <h1 class="sr-only">Neuigkeiten</h1>
        <div v-for="(post, index) in posts" :key="post.id" :class="index % 2 ? 'teaser-odd' : 'teaser-even'">
            <div class="container py-md-5">
                <div class="teaser">
                    <article class="teaser-text" :aria-labelledby="'post-title-' + index">
                        <div
                            v-if="post.onlyVisibleForLoggedInUsers"
                            class="d-inline-block bg-primary px-4 py-1 mt-2 mb-3 text-center w-auto"
                        >
                            <h2 class="text-white m-0 d-inline-block w-auto">&bullet; Community Exklusiv &bullet;</h2>
                        </div>
                        <!-- @formatter:off -->
                        <h2>{{ $formatDate(post.date!, "dd. LLLL yyyy") }} / {{ post.categoryName }}</h2>
                        <!-- @formatter:on -->
                        <h3 :id="'post-title-' + index">
                            {{ post.title }}
                        </h3>
                        <div
                            v-if="!readBlogPostIds.includes(post.id!) && appConfig.user"
                            class="badge rounded-pill bg-warning text-dark unread-badge"
                            aria-label="Ungelesener Beitrag"
                            title="Ungelesen"
                        >
                            Ungelesen
                        </div>
                        <p class="pre-line">
                            {{ post.introText }}
                        </p>

                        <RouterLink class="btn btn-primary" :to="{ name: 'blog-post', params: { id: post.id } }">
                            Zum Artikel
                        </RouterLink>
                    </article>
                    <div class="teaser-image" aria-hidden="true">
                        <img
                            style="max-height: 500px"
                            class="img-fluid mx-auto d-block"
                            :src="post?.presetImageIndex === undefined ? assetLink('/uploads/media/' + post!.imageName) : presetImages[post.presetImageIndex]"
                            alt=""
                        />
                    </div>
                </div>
            </div>
        </div>

        <div v-if="!hasReachedEnd" class="py-5 text-center" role="presentation">
            <button type="button" class="btn btn-primary" @click="loadMore()">Mehr laden</button>
        </div>
    </main>
</template>

<style scoped>
.unread-badge {
    vertical-align: middle;
    font-weight: 600;
    font-size: 0.7rem;
    box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.7);
    padding: 0.25rem 0.5rem;
    margin-bottom: 0.5rem;
}
</style>
